import React, { useEffect, useState } from "react";

// custom components
import { CustomSpinner, Card, NewScreen } from "_atoms";
import { Backbutton } from "_molecules";
import { AirportImage } from "_organisms";

// bootstrap
import Container from "react-bootstrap/Container";
import Row from "react-bootstrap/Row";
import Col from "react-bootstrap/Col";
import Form from "react-bootstrap/Form";

// redux
import { useSelector, useDispatch } from "react-redux";
import * as reviewsSlice from "_redux/slices/analytics/reviews";
import { selectUserDb } from "_redux/slices/main-screen/user";
import {
  getNonPreNotifiedBookingsAll,
  getPreNotifiedBookingsAll,
} from "_redux/slices/analytics/reviews";
import {
  getBookingsDueTimesBetweenAirports,
  selectDueTimesBetweenAirportsAverage,
  selectDueTimesBetweenAirportsLoading,
} from "_redux/slices/prm-bookings/bookings-count";
// utils
import { Colors } from "_styles";

// exterior libraries
import { Title as TremorTitle, Subtitle } from "@tremor/react";
import {
  Metric,
  Text,
  List,
  ListItem,
  Flex,
  Bold,
  Toggle,
  ToggleItem,
  AreaChart,
} from "@tremor/react";
import { DonutChart } from "@tremor/react";
import { DateRangePicker } from "@tremor/react";

// icons
import { BiErrorCircle } from "react-icons/bi";
import { lookupIsLoading } from "_utils/statusUtil";

// calculate percentage for graph legends
function calculatePercentage(total, number) {
  if (total == null || total == 0) {
    return "0";
  }
  return ((number / total) * 100).toFixed(1);
}

function getDateOneMonthAgo(currentDate) {
  // Get the current timestamp
  const currentTimestamp = currentDate.getTime();

  // Calculate the timestamp for 30 days ago (30 * 24 * 60 * 60 * 1000 milliseconds)
  const thirtyDaysAgoTimestamp = currentTimestamp - 2592000000;

  // Create a new date object from the timestamp for 30 days ago
  const thirtyDaysAgo = new Date(thirtyDaysAgoTimestamp);

  return thirtyDaysAgo;
}

// used for displaying airport information
const RowItem = (props) => {
  return (
    <Row>
      <Form.Label style={{ color: Colors.PRM_LIGHT_BLUE }}>
        {props.label}
      </Form.Label>
      <Container>
        <Form.Control
          disabled={true}
          type="name"
          placeholder=""
          value={props.value}
          defaultValue={""}
        />
      </Container>
    </Row>
  );
};

// legend for pie charts
const LegendItem = (props) => {
  return (
    <div style={{ display: "flex" }} className="mt-2">
      <div
        style={{
          borderWidth: 1,
          height: 12,
          width: 12,
          borderRadius: 50,
          backgroundColor: props.color,
          marginTop: 4,
          padding: 8,
        }}
      />
      <div style={{ marginLeft: 8 }}>
        <Text>{props.text}</Text>
      </div>
    </div>
  );
};

const AirportProfile = (props) => {
  //#region states
  const currentDate = new Date();
  const [pieTab, setPieTab] = useState(1);
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [graphStart, setGraphStart] = useState(null);
  const [graphEnd, setGraphEnd] = useState(null);
  const [typeFilter, setTypeFilter] = useState("none");
  const [airportName, setAirportName] = useState("");
  const [userAirport, setUserAirport] = useState("");
  const [error, setError] = useState(false);

  const [value, setValue] = useState([
    getDateOneMonthAgo(currentDate),
    new Date(),
  ]);
  //#endregion

  //#region redux
  const dispatch = useDispatch();

  // airport information & user information
  const selectedAirport = useSelector(reviewsSlice.selectAirport);
  const airportInformation = useSelector(reviewsSlice.selectAirportInformation);
  const dbUser = useSelector(selectUserDb);

  // loading status
  const status = useSelector(reviewsSlice.selectAirportStatus);
  const errorMessage = useSelector(reviewsSlice.selectAirportAnalyticsError);

  // total prm passenger information
  const twoAirportBookingsApp = useSelector(
    reviewsSlice.selectTwoAirportBookingsApp
  );
  const twoAirportBookingsPortal = useSelector(
    reviewsSlice.selectTwoAirportBookingsPortal
  );
  const twoAirportBookingsSita = useSelector(
    reviewsSlice.selectTwoAirportBookingsSita
  );
  const twoAirportBookingsAll = useSelector(
    reviewsSlice.selectTwoAirportBookingsAll
  );

  // pie chart information
  const departureOnlyBookings = useSelector(
    reviewsSlice.selectDepartureOnlyBookings
  );
  const arrivalsOnlyBookings = useSelector(
    reviewsSlice.selectArrivalsOnlyBookings
  );
  const departureAndArrivalBookings = useSelector(
    reviewsSlice.selectDepartureAndArrivalBookings
  );

  // pre notified pie char
  const preNotifiedAll = useSelector(reviewsSlice.selectPreNotifiedAll);
  const nonPreNotifiedAll = useSelector(reviewsSlice.selectNonPreNotifiedAll);
  const preNotifiedSita = useSelector(reviewsSlice.selectPreNotifiedSita);
  const nonPreNotifiedSita = useSelector(reviewsSlice.selectNonPreNotifiedSita);
  const preNotifiedApp = useSelector(reviewsSlice.selectPreNotifiedApp);
  const nonPreNotifiedApp = useSelector(reviewsSlice.selectNonPreNotifiedApp);
  const preNotifiedPortal = useSelector(reviewsSlice.selectPreNotifiedPortal);
  const nonPreNotifiedPortal = useSelector(
    reviewsSlice.selectNonPreNotifiedPortal
  );

  // graph selectors
  const graphBookings = useSelector(reviewsSlice.selectGraphBookings);

  // dueTimes between airports average
  const dueTimesBetweenAirportsAverage = useSelector(
    selectDueTimesBetweenAirportsAverage
  );
  const dueTimesBetweenAirportsLoading = useSelector(
    selectDueTimesBetweenAirportsLoading
  );

  // loading
  const twoAirportBookingsAllIsLoading = useSelector((state) =>
    lookupIsLoading(state.reviews.statusObj, ["getTwoAirportBookingsAll"])
  );
  const dataIsLoading = useSelector((state) =>
    lookupIsLoading(state.reviews.statusObj, [
      "getDepartureOnlyBookings",
      "getArrivalsOnlyBookings",
      "getDepartureandArrivalsBookings",
    ])
  );
  const bookingsIsLoading = useSelector((state) =>
    lookupIsLoading(state.reviews.statusObj, [
      "getTwoAirportBookingsApp",
      "getTwoAirportBookingsPortal",
      "getTwoAirportBookingsSITA",
    ])
  );
  const preNotifiedDataIsLoading = useSelector((state) =>
    lookupIsLoading(state.reviews.statusObj, [
      "getPreNotifiedBookingsAll",
      "getNonPreNotifiedBookingsAll",
      "getPreNotifiedBookingsApp",
      "getNonPreNotifiedBookingsApp",
      "getPreNotifiedBookingsPortal",
      "getNonPreNotifiedBookingsPortal",
      "getPreNotifiedBookingsSita",
      "getNonPreNotifiedBookingsSita",
    ])
  );
  const graphBookingsIsLoading = useSelector((state) =>
    lookupIsLoading(
      state.reviews.statusObj,
      {
        none: [
          "getTwoAirportMonthlyBookingsArrivals",
          "getTwoAirportMonthlyBookingsDepartures",
        ],
        A: [
          "getTwoAirportMonthlyBookingsAppArrivals",
          "getTwoAirportMonthlyBookingsAppDepartures",
        ],
        P: [
          "getTwoAirportMonthlyBookingsPortalArrivals",
          "getTwoAirportMonthlyBookingsPortalDepartures",
        ],
        S: [
          "getTwoAirportMonthlyBookingsSitaArrivals",
          "getTwoAirportMonthlyBookingsSitaDepartures",
        ],
      }[typeFilter]
    )
  );

  //#endregion

  //#region time / date string handlers
  // set time period string based on date tab switcher
  useEffect(() => {
    // single day
    if (value[1] == null) {
      setStartDate(value[0].toISOString().split("T")[0]);
      setEndDate(value[0].toISOString().split("T")[0]);
    }
    // set two day date range
    else if (value[1] != null) {
      setStartDate(value[0].toISOString().split("T")[0]);
      setEndDate(value[1].toISOString().split("T")[0]);
    }

    // set graph start time 6 months before
    const current = new Date();
    const endOfCurrentMonth = new Date(
      current.getFullYear(),
      current.getMonth() + 1,
      0
    );
    const startMonthIndex = current.getMonth() - 5;
    const startMonth = new Date(current.getFullYear(), startMonthIndex, 1);
    setGraphStart(startMonth);
    setGraphEnd(endOfCurrentMonth);
  }, [value]);
  //#endregion

  //#region data fetch handlers
  useEffect(() => {
    setError(false);
    if (status == "succeeded") {
      setTimeout(() => {
        setError(false);
      }, 1000);
    }
    if (status == "idle") {
      setTimeout(() => {
        setError(false);
      }, 1000);
    }
    if (status == "failed") {
      setError(true);
    }
  }, [status]);

  useEffect(() => {
    // if user airport -> selected airport
    if (pieTab == 1) {
      if (
        airportInformation &&
        airportInformation[selectedAirport.airport] &&
        airportInformation[selectedAirport.airport].name &&
        startDate &&
        endDate
      ) {
        // request to both
        dispatch(
          reviewsSlice.getDepartureandArrivalsBookings({
            departureAirport: dbUser.details.authority,
            arrivalAirport: airportInformation[selectedAirport.airport].iata,
            startDate: startDate,
            endDate: endDate,
            filter: typeFilter,
          })
        );
        // departures only
        dispatch(
          reviewsSlice.getDepartureOnlyBookings({
            departureAirport: dbUser.details.authority,
            arrivalAirport: airportInformation[selectedAirport.airport].iata,
            startDate: startDate,
            endDate: endDate,
            filter: typeFilter,
          })
        );
        // arrivals only
        dispatch(
          reviewsSlice.getArrivalsOnlyBookings({
            departureAirport: dbUser.details.authority,
            arrivalAirport: airportInformation[selectedAirport.airport].iata,
            startDate: startDate,
            endDate: endDate,
            filter: typeFilter,
          })
        );

        // arrivals only
        dispatch(
          reviewsSlice.getAverageDueTime({
            departureAirport: dbUser.details.authority,
            arrivalAirport: airportInformation[selectedAirport.airport].iata,
            startDate: startDate,
            endDate: endDate,
            filter: typeFilter,
          })
        );

        // dueTimes average
        dispatch(
          getBookingsDueTimesBetweenAirports({
            iata: dbUser.details.authority,
            departureAirportIATA: dbUser.details.authority,
            arrivalAirportIATA:
              airportInformation[selectedAirport.airport].iata,
            date: "2020-01-01",
            endDate: "2024-01-01",
            // date: startDate,
            // endDate: endDate,
          })
        );
      }
    }
    // else selected airport -> user airport
    else {
      if (
        airportInformation &&
        airportInformation[selectedAirport.airport] &&
        airportInformation[selectedAirport.airport].name &&
        startDate &&
        endDate
      ) {
        // get monthly departures on app
        dispatch(
          reviewsSlice.getTwoAirportMonthlyBookingsAppDepartures({
            departureAirport: airportInformation[selectedAirport.airport].iata,
            arrivalAirport: dbUser.details.authority,
            startMonth: graphStart,
            endMonth: graphEnd,
            filter: typeFilter,
            type: "Departures",
          })
        );
        // get monthly arrivals on app
        dispatch(
          reviewsSlice.getTwoAirportMonthlyBookingsAppArrivals({
            departureAirport: dbUser.details.authority,
            arrivalAirport: airportInformation[selectedAirport.airport].iata,
            startMonth: graphStart,
            endMonth: graphEnd,
            filter: typeFilter,
            type: "Arrivals",
          })
        );
        // get monthly departures on portal
        dispatch(
          reviewsSlice.getTwoAirportMonthlyBookingsPortalDepartures({
            departureAirport: airportInformation[selectedAirport.airport].iata,
            arrivalAirport: dbUser.details.authority,
            startMonth: graphStart,
            endMonth: graphEnd,
            filter: typeFilter,
            type: "Departures",
          })
        );
        // get monthly arrivals on portal
        dispatch(
          reviewsSlice.getTwoAirportMonthlyBookingsPortalArrivals({
            departureAirport: dbUser.details.authority,
            arrivalAirport: airportInformation[selectedAirport.airport].iata,
            startMonth: graphStart,
            endMonth: graphEnd,
            filter: typeFilter,
            type: "Arrivals",
          })
        );

        // dueTimes average
        dispatch(
          getBookingsDueTimesBetweenAirports({
            iata: dbUser.details.authority,
            departureAirportIATA:
              airportInformation[selectedAirport.airport].iata,
            arrivalAirportIATA: dbUser.details.authority,
            // date: startDate,
            // endDate: endDate,
            date: "2020-01-01",
            endDate: "2024-01-01",
          })
        );
      }
    }
  }, [airportInformation, pieTab, startDate, endDate, typeFilter]);

  useEffect(() => {
    let departureAirport = dbUser.details.authority;
    let arrivalAirport = airportInformation[selectedAirport.airport]?.iata;
    if (pieTab == 2) {
      console.log("here");
      departureAirport = airportInformation[selectedAirport.airport].iata;
      arrivalAirport = dbUser.details.authority;
    }
    if (
      airportInformation &&
      airportInformation[selectedAirport.airport] &&
      airportInformation[selectedAirport.airport].name &&
      startDate &&
      endDate
    ) {
      // get monthly departures on app
      dispatch(
        reviewsSlice.getTwoAirportMonthlyBookingsAppDepartures({
          departureAirport: departureAirport,
          arrivalAirport: arrivalAirport,
          startMonth: graphStart,
          endMonth: graphEnd,
          filter: typeFilter,
          type: "Departures",
        })
      );
      // get monthly arrivals on app
      dispatch(
        reviewsSlice.getTwoAirportMonthlyBookingsAppArrivals({
          departureAirport: departureAirport,
          arrivalAirport: arrivalAirport,
          startMonth: graphStart,
          endMonth: graphEnd,
          filter: typeFilter,
          type: "Arrivals",
        })
      );
      // get monthly departures on portal
      dispatch(
        reviewsSlice.getTwoAirportMonthlyBookingsPortalDepartures({
          departureAirport: departureAirport,
          arrivalAirport: arrivalAirport,
          startMonth: graphStart,
          endMonth: graphEnd,
          filter: typeFilter,
          type: "Departures",
        })
      );
      // get monthly arrivals on portal
      dispatch(
        reviewsSlice.getTwoAirportMonthlyBookingsPortalArrivals({
          departureAirport: departureAirport,
          arrivalAirport: arrivalAirport,
          startMonth: graphStart,
          endMonth: graphEnd,
          filter: typeFilter,
          type: "Arrivals",
        })
      );
      // get all bookings

      dispatch(
        getNonPreNotifiedBookingsAll({
          departureAirport: departureAirport,
          arrivalAirport: arrivalAirport,
          startDate: startDate,
          endDate: endDate,
        })
      );

      console.log("here22");
      dispatch(
        getPreNotifiedBookingsAll({
          departureAirport: departureAirport,
          arrivalAirport: arrivalAirport,
          startDate: startDate,
          endDate: endDate,
        })
      );
      // console.log('here23')
      dispatch(
        reviewsSlice.getPreNotifiedBookingsApp({
          departureAirport: departureAirport,
          arrivalAirport: arrivalAirport,
          startDate: startDate,
          endDate: endDate,
        })
      );

      dispatch(
        reviewsSlice.getNonPreNotifiedBookingsApp({
          departureAirport: departureAirport,
          arrivalAirport: arrivalAirport,
          startDate: startDate,
          endDate: endDate,
        })
      );

      dispatch(
        reviewsSlice.getPreNotifiedBookingsPortal({
          departureAirport: departureAirport,
          arrivalAirport: arrivalAirport,
          startDate: startDate,
          endDate: endDate,
        })
      );

      dispatch(
        reviewsSlice.getNonPreNotifiedBookingsPortal({
          departureAirport: departureAirport,
          arrivalAirport: arrivalAirport,
          startDate: startDate,
          endDate: endDate,
        })
      );

      dispatch(
        reviewsSlice.getPreNotifiedBookingsSita({
          departureAirport: departureAirport,
          arrivalAirport: arrivalAirport,
          startDate: startDate,
          endDate: endDate,
        })
      );

      dispatch(
        reviewsSlice.getNonPreNotifiedBookingsSita({
          departureAirport: departureAirport,
          arrivalAirport: arrivalAirport,
          startDate: startDate,
          endDate: endDate,
        })
      );

      dispatch(
        reviewsSlice.getTwoAirportBookingsAll({
          departureAirport: departureAirport,
          arrivalAirport: arrivalAirport,
          startDate: startDate,
          endDate: endDate,
        })
      );
      // // get all app bookings
      dispatch(
        reviewsSlice.getTwoAirportBookingsApp({
          departureAirport: departureAirport,
          arrivalAirport: arrivalAirport,
          startDate: startDate,
          endDate: endDate,
        })
      );
      // // get all portal bookings
      dispatch(
        reviewsSlice.getTwoAirportBookingsPortal({
          departureAirport: departureAirport,
          arrivalAirport: arrivalAirport,
          startDate: startDate,
          endDate: endDate,
        })
      );
      // // get all Sita bookings
      dispatch(
        reviewsSlice.getTwoAirportBookingsSita({
          departureAirport: departureAirport,
          arrivalAirport: arrivalAirport,
          startDate: startDate,
          endDate: endDate,
        })
      );
      // dueTimes average
      dispatch(
        getBookingsDueTimesBetweenAirports({
          iata: dbUser.details.authority,
          departureAirportIATA: departureAirport,
          arrivalAirportIATA: arrivalAirport,
          // date: startDate,
          // endDate: endDate,
          date: "2020-01-01",
          endDate: "2024-01-01",
        })
      );
    }
  }, [airportInformation, pieTab, startDate, endDate]);

  //#endregion

  //#region airports information handlers

  // get airport information
  useEffect(() => {
    if (airportInformation[selectedAirport.airport]) {
    } else {
      dispatch(reviewsSlice.getAirportInformation(selectedAirport.airport));
    }
  }, []);

  // get or set airport name
  useEffect(() => {
    if (
      airportInformation[dbUser.details.authority] &&
      airportInformation[dbUser.details.authority].name
    ) {
      setUserAirport(airportInformation[dbUser.details.authority].name);
    } else {
      // retrieve local airport info
      dispatch(reviewsSlice.getAirportInformation(dbUser.details.authority));
    }
  }, [dbUser]);

  // set selected airport name
  useEffect(() => {
    if (
      airportInformation &&
      airportInformation[selectedAirport.airport] &&
      airportInformation[selectedAirport.airport].name
    ) {
      setAirportName(airportInformation[selectedAirport.airport].name);
    }
    if (
      airportInformation[dbUser.details.authority] &&
      airportInformation[dbUser.details.authority].name
    ) {
      setUserAirport(airportInformation[dbUser.details.authority].name);
    }
  }, [airportInformation]);

  //#endregion

  //#region data
  const data1 = [
    {
      type: "Only Departure Assistance Request",
      bookings: departureOnlyBookings,
    },
    {
      type: "Only Arrival Assistance Request",
      bookings: arrivalsOnlyBookings,
    },
    {
      type: "Both Assistance Request",
      bookings: departureAndArrivalBookings,
    },
  ];

  const data2 = [
    {
      type: "Only Departure Assistance Request",
      bookings: departureOnlyBookings,
    },
    {
      type: "Only Arrival Assistance Request",
      bookings: arrivalsOnlyBookings,
    },
    {
      type: "Both Assistance Request",
      bookings: departureAndArrivalBookings,
    },
  ];

  const bookings = [
    {
      name: "PRM Assist App",
      stat: twoAirportBookingsApp,
    },
    {
      name: "PRM Assist Portal",
      stat: twoAirportBookingsPortal,
    },
    {
      name: "PRM Assist + SITA",
      stat: twoAirportBookingsSita,
    },
  ];

  const sourceData = [
    {
      type: "App",
      bookings: twoAirportBookingsApp,
    },
    {
      type: "Portal",
      bookings: twoAirportBookingsPortal,
    },
    {
      type: "All Bookings",
      bookings: twoAirportBookingsAll,
    },
  ];

  const preNotifiedAppObject = [
    {
      type: "Pre-notified",
      bookings: preNotifiedApp,
    },
    {
      type: "Non Pre-notified",
      bookings: nonPreNotifiedApp,
    },
  ];

  const preNotifiedPortalObject = [
    {
      type: "Pre-notified",
      bookings: preNotifiedPortal,
    },
    {
      type: "Non Pre-notified",
      bookings: nonPreNotifiedPortal,
    },
  ];

  const preNotifiedSitaObject = [
    {
      type: "Pre-notified",
      bookings: preNotifiedSita,
    },
    {
      type: "Non Pre-notified",
      bookings: nonPreNotifiedSita,
    },
  ];

  const preNotifiedAllObject = [
    {
      type: "Pre-notified",
      bookings: preNotifiedAll,
    },
    {
      type: "Non Pre-notified",
      bookings: nonPreNotifiedAll,
    },
  ];

  //#endregion

  return (
    <>
      <NewScreen>
        {/* Backbutton */}
        {/* <Row>
          <Col xs={2} className="mt-3">
            <Backbutton
              onClick={() => {
                //   dispatch(reviewsSlice.showAirport(false));
                page = "global-sla";
              }}
            />
          </Col>
        </Row> */}

        {/* Airport Title */}
        <Row style={{ marginTop: -18 }}>
          <Col>
            <Flex>
              <div>
                <Subtitle>
                  {airportInformation &&
                  airportInformation[selectedAirport.airport]
                    ? airportInformation[selectedAirport.airport].iata
                    : ""}
                </Subtitle>
                <TremorTitle>
                  {airportInformation &&
                  airportInformation[selectedAirport.airport]
                    ? airportInformation[selectedAirport.airport].name
                    : ""}
                </TremorTitle>
                <Subtitle>
                  {airportInformation &&
                  airportInformation[selectedAirport.airport]
                    ? airportInformation[selectedAirport.airport].locationServed
                    : ""}
                </Subtitle>
              </div>
              <AirportImage
                iata={
                  airportInformation &&
                  airportInformation[selectedAirport.airport]
                    ? airportInformation[selectedAirport.airport].iata
                    : ""
                }
              />
            </Flex>
          </Col>
        </Row>

        {/* Filters */}
        <Row>
          {/* Airport to Airport Tab */}
          <Col>
            <div>
              <Toggle
                color="zinc"
                defaultValue={1}
                onValueChange={(value) => setPieTab(value)}
              >
                <ToggleItem
                  value={1}
                  text={userAirport + " to " + airportName}
                />
                <ToggleItem
                  value={2}
                  text={airportName + " to " + userAirport}
                />
              </Toggle>
            </div>
          </Col>

          {/* App / Portal / Both */}
          <Col>
            <Toggle
              color="zinc"
              defaultValue={"none"}
              onValueChange={(value) => setTypeFilter(value)}
            >
              <ToggleItem value={"none"} text={"All Bookings"} />
              <ToggleItem value={"A"} text="App" />
              <ToggleItem value={"P"} text="Portal" />
              <ToggleItem value={"S"} text="SITA" />
            </Toggle>
          </Col>

          {/* Date Time Range */}
          <Col>
            <DateRangePicker
              onValueChange={(value) => {
                setValue(value);
              }}
              value={value}
              enableDropdown={true}
              maxWidth="max-w-lg"
              dropdownPlaceholder={"Last 30 Days"}
            />
          </Col>
        </Row>

        {
          /* Error handler */
          error ? (
            <Row className="row justify-content-center mt-5">
              <BiErrorCircle size={38} color={"red"} />
              <div style={{ justifyContent: "center", textAlign: "center" }}>
                <h3>{"Failed to get data"}</h3>
                <p>{"Looks like something went wrong, here are the details"}</p>
                <p>{errorMessage}</p>
              </div>
            </Row>
          ) : (
            /* Loading Bar */
            <>
              {/* Total Passengers & Pie Charts */}
              <Row className="mt-4">
                {/* Total PRM Passengers Card */}
                <Col>
                  <Card>
                    <TremorTitle>Total PRM Passengers</TremorTitle>

                    {/* twoAirportBookingsAll should already include App and Portal Bookings? */}
                    {twoAirportBookingsAllIsLoading ? (
                      <Row className="justify-content-md-center mt-5">
                        {/* <CustomSpinner /> */}
                      </Row>
                    ) : (
                      <Metric>{twoAirportBookingsAll}</Metric>
                    )}

                    <Flex>
                      <Text>
                        <Bold>{"Type"}</Bold>
                      </Text>
                      <Text>
                        <Bold>{"Booking Requests"}</Bold>
                      </Text>
                    </Flex>

                    {bookingsIsLoading ? (
                      <Row className="justify-content-md-center mt-5">
                        {/* <CustomSpinner /> */}
                      </Row>
                    ) : (
                      <List>
                        {bookings.map((country) => (
                          <ListItem>
                            <Flex>
                              <Text truncate={true}>{country.name}</Text>
                              <Text>{country.stat}</Text>
                            </Flex>
                          </ListItem>
                        ))}
                      </List>
                    )}
                  </Card>

                  {/* Average Arrival Time */}
                  <Card className="mt-3">
                    <TremorTitle>Estimated Check-in Time</TremorTitle>
                    {/* {
                        averageDueTime == 0 ? <p style={{fontWeight:'600'}}>No Data</p> : 
                        (averageDueTimeIsLoading ?  
                        <Row className="justify-content-md-center mt-5">
                            <CustomSpinner />
                        </Row>
                        : 
                        <Metric marginTop='mt-1.5'>{averageDueTime.toString()}</Metric>)
                        } */}
                    {dueTimesBetweenAirportsAverage == 0 ? (
                      <p style={{ fontWeight: "600" }}>No Data</p>
                    ) : dueTimesBetweenAirportsLoading === "loading" ? (
                      <Row className="justify-content-md-center mt-5">
                        {/* <CustomSpinner /> */}
                      </Row>
                    ) : (
                      <Metric marginTop="mt-1.5">
                        {dueTimesBetweenAirportsAverage.toString()}
                      </Metric>
                    )}
                    <Text marginTop={"mt-2"}>
                      The duration of time that a passenger will arrive in the
                      airport before their flight departure when travelling from{" "}
                      {userAirport} to {airportName}
                    </Text>
                  </Card>

                  {/*
                    <div className='mt-2'>
                        <Card>
                            <Text><Bold>{'Passenger Check-in Time'}</Bold></Text>
                            {averageDueTime == 0 ? <p style={{fontWeight:'600'}}>Not Data</p> : <Metric>{averageDueTime.toString()}</Metric>}
                            <Text marginTop={averageDueTime == 0 ? 'mt-2' : ''}>Estimated time before the flight that a passenger will arrive in the airport</Text>
                        </Card>
                    </div>*/}
                </Col>

                {/* Pie Charts */}
                <Col xs={9}>
                  {/* Passenger Assistance Trends */}
                  <Card>
                    <TremorTitle>{"Passenger Behavior Statistics"}</TremorTitle>

                    <Row>
                      <Col>
                        {/* Chart Labels */}
                        <Row>
                          {typeFilter != "S" && (
                            <Col xs={7}>
                              <Text>{"Passenger Assistance Trends"}</Text>
                            </Col>
                          )}
                          <Col>
                            <Text>{"Pre-notified Passengers"}</Text>
                          </Col>
                        </Row>

                        {/* Passenger Behavior Chart Donut Chart */}
                        <Row className="mt-3">
                          {/* Pie */}
                          {typeFilter != "S" && (
                            <Col>
                              {departureOnlyBookings == 0 &&
                              arrivalsOnlyBookings == 0 &&
                              departureAndArrivalBookings == 0 ? (
                                <Row className="row justify-content-center mt-1">
                                  <Container className="d-flex justify-content-center align-items-center mt-5">
                                    <p
                                      style={{
                                        opacity: 0.4,
                                        fontWeight: "300",
                                      }}
                                    >
                                      No Data
                                    </p>
                                  </Container>
                                </Row>
                              ) : dataIsLoading ? (
                                <Row className="justify-content-md-center mt-5">
                                  {/* <CustomSpinner /> */}
                                </Row>
                              ) : (
                                <DonutChart
                                  data={pieTab == 1 ? data1 : data2}
                                  category="bookings"
                                  dataKey="type"
                                  colors={[
                                    "blue",
                                    "green",
                                    "amber",
                                    "rose",
                                    "cyan",
                                    "amber",
                                  ]}
                                  showLabel={true}
                                  showTooltip={true}
                                  showAnimation={true}
                                  height="h-44"
                                  marginTop="mt-0"
                                  variant={"pie"}
                                />
                              )}
                            </Col>
                          )}

                          {/* Passenger Behavior Chart Donut Chart */}
                          <Col>
                            {false ? (
                              <Row className="row justify-content-center mt-1">
                                <Container className="d-flex justify-content-center align-items-center mt-5">
                                  <p
                                    style={{ opacity: 0.4, fontWeight: "300" }}
                                  >
                                    No Data
                                  </p>
                                </Container>
                              </Row>
                            ) : preNotifiedDataIsLoading ? (
                              <Row className="justify-content-md-center mt-5">
                                {/* <CustomSpinner /> */}
                              </Row>
                            ) : (
                              <DonutChart
                                data={
                                  {
                                    none: preNotifiedAllObject,
                                    A: preNotifiedAppObject,
                                    P: preNotifiedPortalObject,
                                    S: preNotifiedSitaObject,
                                  }[typeFilter]
                                }
                                category="bookings"
                                dataKey="type"
                                colors={["blue", "red", "amber"]}
                                // variant="pie"
                                label={undefined}
                                showLabel={true}
                                showTooltip={true}
                                showAnimation={true}
                                height="h-44"
                                marginTop="mt-0"
                                variant={"pie"}
                              />
                            )}
                          </Col>
                        </Row>

                        <Row>
                          {/* Legend */}
                          {typeFilter != "S" && (
                            <Col className="mt-3" xs={7}>
                              <LegendItem
                                text={
                                  pieTab == 1
                                    ? calculatePercentage(
                                        departureAndArrivalBookings +
                                          departureOnlyBookings +
                                          arrivalsOnlyBookings,
                                        departureOnlyBookings
                                      ) +
                                      "% of bookings requested assistance only in " +
                                      userAirport +
                                      " (" +
                                      departureOnlyBookings +
                                      ")"
                                    : "% of bookings requested assistance only in " +
                                      airportName +
                                      " (" +
                                      departureOnlyBookings +
                                      ")"
                                }
                                color={Colors.PRM_LIGHT_BLUE}
                              />
                              <LegendItem
                                text={
                                  pieTab == 1
                                    ? calculatePercentage(
                                        departureAndArrivalBookings +
                                          departureOnlyBookings +
                                          arrivalsOnlyBookings,
                                        arrivalsOnlyBookings
                                      ) +
                                      "% of bookings that requested assistance only in " +
                                      airportName +
                                      " (" +
                                      arrivalsOnlyBookings +
                                      ")"
                                    : "% of bookings requested assistance only in " +
                                      userAirport +
                                      " (" +
                                      arrivalsOnlyBookings +
                                      ")"
                                }
                                color={"green"}
                              />
                              <LegendItem
                                text={
                                  calculatePercentage(
                                    departureAndArrivalBookings +
                                      departureOnlyBookings +
                                      arrivalsOnlyBookings,
                                    departureAndArrivalBookings
                                  ) +
                                  "% of bookings that requested assistance in Both " +
                                  userAirport +
                                  " & " +
                                  airportName +
                                  " (" +
                                  departureAndArrivalBookings +
                                  ")"
                                }
                                color={"orange"}
                              />
                            </Col>
                          )}

                          {/* Legend */}
                          {typeFilter != "S" ? (
                            <>
                              <Col className="mt-3">
                                <LegendItem
                                  text={`pre-notified bookings: ${
                                    {
                                      none: preNotifiedAll,
                                      A: preNotifiedApp,
                                      P: preNotifiedPortal,
                                      S: preNotifiedSita,
                                    }[typeFilter]
                                  }`}
                                  color={Colors.PRM_LIGHT_BLUE}
                                />
                                <LegendItem
                                  text={`non pre-notified bookings: ${
                                    {
                                      none: nonPreNotifiedAll,
                                      A: nonPreNotifiedApp,
                                      P: nonPreNotifiedPortal,
                                      S: nonPreNotifiedSita,
                                    }[typeFilter]
                                  }`}
                                  color={"red"}
                                />
                              </Col>
                            </>
                          ) : (
                            <>
                              <Row>
                                <LegendItem
                                  text={`pre-notified bookings: ${
                                    {
                                      none: preNotifiedAll,
                                      A: preNotifiedApp,
                                      P: preNotifiedPortal,
                                      S: preNotifiedSita,
                                    }[typeFilter]
                                  }`}
                                  color={Colors.PRM_LIGHT_BLUE}
                                />
                                <LegendItem
                                  text={`non pre-notified bookings: ${
                                    {
                                      none: nonPreNotifiedAll,
                                      A: nonPreNotifiedApp,
                                      P: nonPreNotifiedPortal,
                                      S: nonPreNotifiedSita,
                                    }[typeFilter]
                                  }`}
                                  color={"red"}
                                />
                              </Row>
                            </>
                          )}
                        </Row>
                      </Col>
                    </Row>
                  </Card>
                </Col>
              </Row>

              {/* Line Graph */}
              <Row className="mt-3">
                <Col>
                  <Card>
                    <TremorTitle>
                      {
                        { none: " ", A: "App", P: "Portal", S: "SITA" }[
                          typeFilter
                        ]
                      }{" "}
                      Passenger Trends
                    </TremorTitle>
                    <Text>
                      {
                        "Identify trends between the number of passengers travelling from your airport and "
                      }
                      {airportInformation &&
                      airportInformation[selectedAirport.airport]
                        ? airportInformation[selectedAirport.airport].name
                        : ""}
                    </Text>
                    {graphBookingsIsLoading ? (
                      <Row className="justify-content-md-center mt-5">
                        {/* <CustomSpinner /> */}
                      </Row>
                    ) : (
                      <AreaChart
                        data={graphBookings}
                        dataKey="month"
                        categories={["Departures", "Arrivals"]}
                        colors={["blue", "green"]}
                        marginTop="mt-4"
                        yAxisWidth="w-10"
                      />
                    )}
                  </Card>
                </Col>
              </Row>

              {/* Airport Information */}
              <Row className="mt-5" style={{ marginBottom: 50 }}>
                <Col>
                  <Card>
                    <div style={{ marginBottom: 6 }}>
                      <TremorTitle>Airport Information</TremorTitle>
                    </div>
                    <Row>
                      <Col>
                        <RowItem
                          label={"ICAO"}
                          value={
                            airportInformation &&
                            airportInformation[selectedAirport.airport]
                              ? airportInformation[selectedAirport.airport].icao
                              : ""
                          }
                        />
                      </Col>
                      <Col>
                        <RowItem
                          label={"Email Address"}
                          value={
                            airportInformation &&
                            airportInformation[selectedAirport.airport]
                              ? airportInformation[selectedAirport.airport]
                                  .airportAuthorityEmailAddress
                              : ""
                          }
                        />
                      </Col>
                      <Col>
                        <RowItem
                          label={"Time Zone"}
                          value={
                            airportInformation &&
                            airportInformation[selectedAirport.airport]
                              ? airportInformation[selectedAirport.airport]
                                  .timeZone
                              : ""
                          }
                        />
                      </Col>
                    </Row>
                    <Row className="mt-4">
                      <Col>
                        <RowItem
                          label={"IATA"}
                          value={
                            airportInformation &&
                            airportInformation[selectedAirport.airport]
                              ? airportInformation[selectedAirport.airport].iata
                              : ""
                          }
                        />
                      </Col>
                      <Col>
                        <RowItem
                          label={"Airport Name"}
                          value={
                            airportInformation &&
                            airportInformation[selectedAirport.airport]
                              ? airportInformation[selectedAirport.airport].name
                              : ""
                          }
                        />
                      </Col>
                      <Col>
                        <RowItem
                          label={"Location Served"}
                          value={
                            airportInformation &&
                            airportInformation[selectedAirport.airport]
                              ? airportInformation[selectedAirport.airport]
                                  .locationServed
                              : ""
                          }
                        />
                      </Col>
                    </Row>
                    <Row className="mt-4">
                      <Col>
                        <RowItem
                          label={"Service Provider Email"}
                          value={
                            airportInformation &&
                            airportInformation[selectedAirport.airport]
                              ? airportInformation[selectedAirport.airport]
                                  .serviceProviderEmail
                              : ""
                          }
                        />
                      </Col>
                      <Col>
                        <RowItem
                          label={"Service Provider Phone"}
                          value={
                            airportInformation &&
                            airportInformation[selectedAirport.airport]
                              ? airportInformation[selectedAirport.airport]
                                  .serviceProviderPhone
                              : ""
                          }
                        />
                      </Col>
                      <Col>
                        <Row>
                          <Form.Label style={{ color: Colors.PRM_LIGHT_BLUE }}>
                            {"PRM Website"}
                          </Form.Label>
                          <div style={{ cursor: "pointer" }}>
                            <Form.Control
                              disabled={true}
                              type="name"
                              placeholder=""
                              value={
                                airportInformation &&
                                airportInformation[selectedAirport.airport]
                                  ? airportInformation[selectedAirport.airport]
                                      .airportAuthorityPRMUrl
                                  : ""
                              }
                              defaultValue={""}
                            />
                          </div>
                        </Row>
                      </Col>
                    </Row>
                  </Card>
                </Col>
              </Row>
            </>
          )
        }
      </NewScreen>
    </>
  );
};

export default AirportProfile;
